import { findTagIconByType, formatVideoTime } from '~/assets/util/helpers/tags'
import { formatDate } from '~/assets/util/helpers'

export const promiseSettledFormatResponse = (params) => {
	return params.map(res => res.status === 'fulfilled' ? res.value : null)
}

export const eventShareRequestUrlsMap = {
	hd: id => `/api/v1/events/${id}/share`,
	pano: id => `/api/v1/events/${id}/share`,
	event: id => `/api/v1/events/${id}/share`,
	clip: id => `/api/v1/clips/${id}/share`,
	autohighlight: id => `/api/v1/events/highlights/${id}/share`,
	'player-highlight': id => `/api/v1/player-highlights/${id}/share`
}

export const eventExportRequestUrlsMap = {
	hd: id => `/api/v1/events/${id}/export`,
	pano: id => `/api/v1/events/${id}/export`,
	event: id => `/api/v1/events/${id}/export`,
	highlight: id => `/api/v1/events/highlights/${id}/export`,
	autohighlight: id => `/api/v1/events/highlights/${id}/export`,
	clip: id => `/api/v1/clips/${id}/export`,
	'player-highlight': id => `/api/v1/player-highlights/${id}/export`
}

export const eventRequestUrlsMap = {
	hd: id => `/api/v1/events/${id}`,
	pano: id => `/api/v1/events/${id}`,
	event: id => `/api/v1/events/${id}`,
	clip: id => `/api/v1/users/me/clips/${id}`,
	autohighlight: id => `/api/v1/events/highlights/${id}`,
	'player-highlight': id => `/api/v1/player-highlights/${id}`
}

export const formatPlayerPayload = (payload, preset = {}) => {
	const defaultStatus = 'vod'
	const outputData = {
		id: payload.id,
		eventId: preset.eventId || '',
		type: (payload.attributes && payload.attributes.type) ? payload.attributes.type : payload.type,
		title: (payload.attributes && payload.attributes.name) || '',
		poster: '',
		source: {
			hd: '',
			hdThumbnails: null,
			pano: '',
			panoThumbnails: null
		},
		clubId: payload.attributes?.clubId || '',
		userId: payload.attributes?.userId || '',
		originalItem: payload,
		meta: {
			playerReady: false,
			sourceError: false
		},
		viewsNumber: payload.attributes?.statistics?.viewCount || 0
	}

	switch (payload.type) {
			case 'event':
				outputData.eventId = payload.id
				outputData.poster = payload?.attributes.thumbnails.hd
				outputData.sportType = payload.attributes.gameInfo.name
				outputData.source.hd = payload.attributes.urls.hd
				outputData.source.hdMp4 = payload.attributes.urls.hdMP4
				outputData.source.hdThumbnails = payload.attributes.previewThumbnails?.hd
				outputData.source.pano = payload.attributes.urls.pano
				outputData.source.panoMp4 = payload.attributes.urls.panoMP4
				outputData.source.panoThumbnails = payload.attributes.previewThumbnails?.pano
				outputData.meta.playlist = (payload.attributes.highlights || [])
					.map(highlight => formatPlayerPayload(highlight, { eventId: payload.id }))
				outputData.gameInfo = payload.attributes.gameInfo
				outputData.startDate = formatDate(payload.attributes.startDateTime, 'long')
				outputData.status = payload.attributes?.status || defaultStatus
				break
			case 'clip':
				// this logic changed on BE, for Atlit cases it should always be event because we are using targetId users/me/clips
				outputData.eventId = payload.attributes.target === 'event'
					? payload.attributes.targetId
					: ''
				outputData.source.hd = payload.attributes.urls?.clip
				outputData.source.hdMp4 = payload.attributes.downloadUrls?.clip
				outputData.meta.status = payload.attributes.status
				outputData.poster = payload.attributes ? payload.attributes.urls.thumbnail : (payload.thumbnail || outputData.poster)
				outputData.startDate = formatDate(payload.meta.createdAt, 'long')
				break
			case 'highlight':
			case 'autohighlight':
				outputData.source.hdThumbnails = payload.attributes ? payload.attributes.previewThumbnail : payload.previewThumbnail
				outputData.eventId = payload.attributes ? payload.attributes.eventId : outputData.eventId
				outputData.poster = payload.attributes ? payload.attributes.urls.thumbnail : (payload.thumbnail || outputData.poster)
				outputData.source.hd = payload.attributes ? payload.attributes.urls.highlight : payload.url
				outputData.source.hdMp4 = payload.attributes ? payload.attributes?.downloadUrls?.highlight : payload.downloadUrl
				outputData.viewsNumber = payload.attributes ? payload.attributes?.statistics?.viewCount : (payload.statistics.viewCount || 0)
				break
			case 'player-highlight':
				outputData.eventId = payload.attributes ? payload.attributes.eventId : outputData.eventId
				outputData.poster = payload.attributes ? payload.attributes.urls?.thumbnail : (payload.thumbnail || outputData.poster)
				outputData.source.hd = payload.attributes ? payload.attributes.urls?.highlight : payload.url
				outputData.source.hdMp4 = payload.attributes?.downloadUrls?.highlight
				outputData.meta.team = payload.attributes.team
				break
	}

	return outputData
}

export const formatEventTagData = (tag, sportType, included = []) => {
	return {
		id: tag.id,
		type: tag.attributes.type,
		name: tag.attributes.name || tag.attributes.type,
		player: tag.attributes.player || null,
		startTime: tag.attributes.startTime,
		startTimeFormatted: formatVideoTime(tag.attributes.startTime),
		endTime: tag.attributes.endTime,
		iconName: findTagIconByType(tag.attributes.type, sportType),
		taggedUsers: included.filter(item => Number(item.attributes.targetId) === tag.id)
	}
}

export const formatNewEventTagData = (tag, sportType) => {
	return {
		...tag,
		startTimeFormatted: formatVideoTime(tag.startTime),
		iconName: findTagIconByType(tag.type, sportType)
	}
}

export const formatYouboraAnalyticsData = (payload, player, user) => {
	const outputData = {
		entityID: payload.id,
		eventID: payload.eventId,
		sportType: payload.sportType,
		contentTitle: payload.title,
		videoMode: player.mode,
		contentIsLive: player.isLive,
		userID: user?.id || null,
		userType: 'viewer'
	}

	switch (payload.type) {
			case 'clip':
				outputData.entityType = 'Clip'
				break
			case 'autohighlight':
				outputData.entityType = 'Game highlight'
				break
			case 'hd':
			case 'event':
				outputData.entityType = 'Event HD'
				break
			case 'pano':
				outputData.entityType = 'Event Pano'
				break
			case 'player-highlight':
				outputData.entityType = 'Player highlight'
				break
	}

	return outputData
}

export const formatPlayerMetadata = (payload) => {
	return {
		title: payload?.title || '',
		description: '',
		gameInfo: payload?.gameInfo || false
	}
}
